import React, { useState } from 'react'
import { data, Form, redirect, useNavigate } from 'react-router'
import { Button } from '#app/components/ui/button.tsx'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { adminCookie, getAdminToken } from '#app/routes/admin.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { cn } from '#app/utils/misc.tsx'
import { pairPlayers, selectByeRecipient } from '#app/utils/pairing.server.ts'
import { computeTiebreaks } from '#app/utils/tiebreak.server.ts'
import { type Route } from './+types/results.ts'

export async function loader({ request }: Route.LoaderArgs) {
	const password = process.env.ADMIN_SECRET
	const cookieValue = await adminCookie.parse(request.headers.get('Cookie'))
	const isAdmin = !!password && cookieValue === getAdminToken(password)

	const params = new URL(request.url).searchParams
	const editing = params.has('edit')
	const creatingNew = params.has('new')
	const selectedId = params.get('id')
	const include = {
		players: { orderBy: { createdAt: 'asc' } },
		winner: true,
		secondPlace: true,
		roundsList: {
			orderBy: { number: 'asc' },
			include: {
				pairings: {
					include: { player1: true, player2: true },
				},
			},
		},
	} as const
	const tournament = selectedId
		? await prisma.tournament.findUnique({ where: { id: selectedId }, include })
		: await prisma.tournament.findFirst({
				orderBy: { createdAt: 'desc' },
				include,
			})
	const allTournaments = await prisma.tournament.findMany({
		select: { id: true, name: true, date: true, finished: true },
		orderBy: { date: 'desc' },
	})
	return { tournament, allTournaments, editing, creatingNew, isAdmin }
}

function computeNewRecord(record: string, won: boolean): string {
	const parts = record.split('-').map(Number)
	const w = parts[0] ?? 0
	const l = parts[1] ?? 0
	return won ? `${w + 1}-${l}` : `${w}-${l + 1}`
}

async function requireAdmin(request: Request) {
	const password = process.env.ADMIN_SECRET
	const cookieValue = await adminCookie.parse(request.headers.get('Cookie'))
	if (!password || cookieValue !== getAdminToken(password)) {
		throw data({ error: 'Unauthorized' }, { status: 403 })
	}
}

export async function action({ request }: Route.ActionArgs) {
	await requireAdmin(request)

	const formData = await request.formData()
	const intent = String(formData.get('intent'))

	switch (intent) {
		case 'create-tournament': {
			const name = String(formData.get('name'))
			const date = new Date(String(formData.get('date')))
			const startTime = String(formData.get('startTime') ?? '') || null
			const rounds = parseInt(String(formData.get('rounds') ?? '4'), 10) || 4
			const matchLengthRaw = parseInt(
				String(formData.get('matchLength') ?? ''),
				10,
			)
			const matchLength = isNaN(matchLengthRaw) ? null : matchLengthRaw
			const newTournament = await prisma.tournament.create({
				data: { name, date, startTime, rounds, matchLength },
			})
			return redirect(`/tournaments/results?id=${newTournament.id}`)
		}
		case 'update-details': {
			const id = String(formData.get('tournamentId'))
			const name = String(formData.get('name'))
			const date = new Date(String(formData.get('date')))
			const startTime = String(formData.get('startTime') ?? '') || null
			const rounds = parseInt(String(formData.get('rounds') ?? '4'), 10) || 4
			const matchLengthRaw = parseInt(
				String(formData.get('matchLength') ?? ''),
				10,
			)
			const matchLength = isNaN(matchLengthRaw) ? null : matchLengthRaw
			await prisma.tournament.update({
				where: { id },
				data: { name, date, startTime, rounds, matchLength },
			})
			return redirect(`/tournaments/results?id=${id}`)
		}
		case 'add-player': {
			const tournamentId = String(formData.get('tournamentId'))
			const name = String(formData.get('playerName')).trim()
			if (!name) return redirect(`/tournaments/results?id=${tournamentId}`)
			const existing = await prisma.player.findMany({
				where: { tournamentId },
				select: { playerNumber: true },
			})
			const maxNumber = existing.reduce(
				(max, p) => Math.max(max, p.playerNumber ?? 0),
				0,
			)
			await prisma.player.create({
				data: { name, tournamentId, playerNumber: maxNumber + 1 },
			})
			return redirect(`/tournaments/results?id=${tournamentId}`)
		}
		case 'toggle-paid': {
			const playerId = String(formData.get('playerId'))
			const paid = formData.get('paid') === 'true'
			const player = await prisma.player.update({
				where: { id: playerId },
				data: { paid },
				select: { tournamentId: true },
			})
			return redirect(`/tournaments/results?id=${player.tournamentId}`)
		}
		case 'remove-player': {
			const playerId = String(formData.get('playerId'))
			const player = await prisma.player.findUnique({
				where: { id: playerId },
				select: { tournamentId: true },
			})
			await prisma.player.delete({ where: { id: playerId } })
			return redirect(`/tournaments/results?id=${player?.tournamentId ?? ''}`)
		}
		case 'drop-player': {
			const playerId = String(formData.get('playerId'))
			const player = await prisma.player.findUnique({
				where: { id: playerId },
				include: { tournament: true },
			})
			if (!player) return redirect('/tournaments/results')

			const activeRound = await prisma.round.findFirst({
				where: {
					tournamentId: player.tournamentId,
					number: player.tournament.currentRound,
					timeEnded: null,
				},
				include: {
					pairings: {
						where: { OR: [{ player1Id: playerId }, { player2Id: playerId }] },
						include: { player1: true, player2: true },
					},
				},
			})

			const activePairing = activeRound?.pairings[0]
			if (activePairing && !activePairing.winnerId) {
				const isP1 = activePairing.player1Id === playerId
				const opponent = isP1 ? activePairing.player2 : activePairing.player1
				if (!opponent.isByePlayer) {
					await prisma.roundPairing.update({
						where: { id: activePairing.id },
						data: { winnerId: opponent.id },
					})
					await prisma.player.update({
						where: { id: opponent.id },
						data: {
							score: { increment: 1 },
							record: computeNewRecord(opponent.record, true),
						},
					})
					await prisma.player.update({
						where: { id: playerId },
						data: { record: computeNewRecord(player.record, false) },
					})
				}
			}

			await prisma.player.update({
				where: { id: playerId },
				data: { droppedOut: true },
			})
			return redirect(`/tournaments/results?id=${player.tournamentId}`)
		}
		case 'start-tournament': {
			const id = String(formData.get('tournamentId'))
			const tournament = await prisma.tournament.findUnique({
				where: { id },
				include: { players: { orderBy: { createdAt: 'asc' } } },
			})
			if (!tournament) return redirect('/tournaments/results')
			const unpaid = tournament.players.some((p) => !p.paid)
			if (unpaid) return redirect(`/tournaments/results?id=${id}`)

			// Ensure all players have playerNumbers (backward compat)
			const needsNumber = tournament.players.filter(
				(p) => p.playerNumber === null,
			)
			if (needsNumber.length > 0) {
				const maxNum = tournament.players.reduce(
					(max, p) => Math.max(max, p.playerNumber ?? 0),
					0,
				)
				await Promise.all(
					needsNumber.map((p, idx) =>
						prisma.player.update({
							where: { id: p.id },
							data: { playerNumber: maxNum + idx + 1 },
						}),
					),
				)
			}

			const players = await prisma.player.findMany({
				where: { tournamentId: id },
				orderBy: { playerNumber: 'asc' },
			})

			await prisma.tournament.update({
				where: { id },
				data: { started: true },
			})

			// Assign random tieBreakScores (shuffle 1..N)
			const tieBreakOrder = players.map((_, i) => i + 1)
			for (let i = tieBreakOrder.length - 1; i > 0; i--) {
				const j = Math.floor(Math.random() * (i + 1))
				;[tieBreakOrder[i], tieBreakOrder[j]] = [
					tieBreakOrder[j]!,
					tieBreakOrder[i]!,
				]
			}
			await Promise.all(
				players.map((p, idx) =>
					prisma.player.update({
						where: { id: p.id },
						data: { tieBreakScore: tieBreakOrder[idx]! },
					}),
				),
			)

			const round = await prisma.round.create({
				data: { number: 1, timeStarted: new Date(), tournamentId: id },
			})

			const playerByNumber = new Map(players.map((p) => [p.playerNumber!, p]))
			let pairingPool = players
			let byePlayer: (typeof players)[number] | null = null

			if (players.length % 2 !== 0) {
				const byePlayerNum = selectByeRecipient(
					players.map((p) => ({
						playerNumber: p.playerNumber!,
						score: p.score,
						hadBye: false,
					})),
				)
				const byeRecipient = playerByNumber.get(byePlayerNum)!
				byePlayer = await prisma.player.create({
					data: {
						name: 'BYE',
						tournamentId: id,
						isByePlayer: true,
						paid: true,
					},
				})
				await prisma.roundPairing.create({
					data: {
						roundId: round.id,
						tournamentId: id,
						player1Id: byeRecipient.id,
						player1RecordBefore: byeRecipient.record,
						player2Id: byePlayer.id,
						player2RecordBefore: '0-0',
						winnerId: byeRecipient.id,
					},
				})
				await prisma.player.update({
					where: { id: byeRecipient.id },
					data: {
						score: { increment: 1 },
						record: computeNewRecord(byeRecipient.record, true),
					},
				})
				pairingPool = players.filter((p) => p.playerNumber !== byePlayerNum)
			}

			const pairingInputs = pairingPool.map((p) => ({
				playerNumber: p.playerNumber!,
				score: p.score,
				opponents: [],
			}))
			const pairs = pairPlayers(pairingInputs)

			await prisma.roundPairing.createMany({
				data: pairs.map(([n1, n2]) => ({
					roundId: round.id,
					tournamentId: id,
					player1Id: playerByNumber.get(n1)!.id,
					player1RecordBefore: playerByNumber.get(n1)!.record,
					player2Id: playerByNumber.get(n2)!.id,
					player2RecordBefore: playerByNumber.get(n2)!.record,
				})),
			})

			return redirect(`/tournaments/results?id=${id}`)
		}
		case 'report-results': {
			const tournamentId = String(formData.get('tournamentId'))
			const roundId = String(formData.get('roundId'))

			const round = await prisma.round.findUnique({
				where: { id: roundId },
				include: { pairings: { include: { player1: true, player2: true } } },
			})
			if (!round) return redirect(`/tournaments/results?id=${tournamentId}`)

			for (const pairing of round.pairings) {
				if (pairing.player2.isByePlayer) continue
				const winnerId = formData.get(`winner_${pairing.id}`)
				if (!winnerId) continue
				const loserId =
					winnerId === pairing.player1Id ? pairing.player2Id : pairing.player1Id
				const winner =
					pairing.player1Id === winnerId ? pairing.player1 : pairing.player2
				const loser =
					pairing.player1Id === loserId ? pairing.player1 : pairing.player2
				await prisma.roundPairing.update({
					where: { id: pairing.id },
					data: { winnerId: String(winnerId) },
				})
				await prisma.player.update({
					where: { id: winner.id },
					data: {
						score: { increment: 1 },
						record: computeNewRecord(winner.record, true),
					},
				})
				await prisma.player.update({
					where: { id: loser.id },
					data: { record: computeNewRecord(loser.record, false) },
				})
			}

			await prisma.round.update({
				where: { id: roundId },
				data: { timeEnded: new Date() },
			})

			return redirect(`/tournaments/results?id=${tournamentId}`)
		}
		case 'next-round': {
			const tournamentId = String(formData.get('tournamentId'))

			const tournament = await prisma.tournament.findUnique({
				where: { id: tournamentId },
				include: {
					players: true,
					pairings: {
						include: { player1: true, player2: true },
					},
				},
			})
			if (!tournament)
				return redirect(`/tournaments/results?id=${tournamentId}`)

			// Build opponents map from all existing non-bye pairings
			const opponentsMap = new Map<string, number[]>()
			const hadByeSet = new Set<string>()
			for (const pairing of tournament.pairings) {
				if (pairing.player2.isByePlayer) {
					hadByeSet.add(pairing.player1Id)
					continue
				}
				if (pairing.player1.isByePlayer) {
					hadByeSet.add(pairing.player2Id)
					continue
				}
				if (!pairing.player1.playerNumber || !pairing.player2.playerNumber)
					continue
				const p1Opps = opponentsMap.get(pairing.player1Id) ?? []
				p1Opps.push(pairing.player2.playerNumber)
				opponentsMap.set(pairing.player1Id, p1Opps)
				const p2Opps = opponentsMap.get(pairing.player2Id) ?? []
				p2Opps.push(pairing.player1.playerNumber)
				opponentsMap.set(pairing.player2Id, p2Opps)
			}

			const newRoundNumber = tournament.currentRound + 1
			await prisma.tournament.update({
				where: { id: tournamentId },
				data: { currentRound: newRoundNumber },
			})

			const round = await prisma.round.create({
				data: {
					number: newRoundNumber,
					timeStarted: new Date(),
					tournamentId,
				},
			})

			const activePlayers = tournament.players.filter(
				(p) => !p.droppedOut && !p.isByePlayer && p.playerNumber !== null,
			)
			const playerByNumber = new Map(
				tournament.players.map((p) => [p.playerNumber!, p]),
			)

			let pairingPool = activePlayers
			if (activePlayers.length % 2 !== 0) {
				const byePlayerNum = selectByeRecipient(
					activePlayers.map((p) => ({
						playerNumber: p.playerNumber!,
						score: p.score,
						hadBye: hadByeSet.has(p.id),
					})),
				)
				const byeRecipient = playerByNumber.get(byePlayerNum)!
				let byePlayer = tournament.players.find((p) => p.isByePlayer) ?? null
				if (!byePlayer) {
					byePlayer = await prisma.player.create({
						data: { name: 'BYE', tournamentId, isByePlayer: true, paid: true },
					})
				}
				await prisma.roundPairing.create({
					data: {
						roundId: round.id,
						tournamentId,
						player1Id: byeRecipient.id,
						player1RecordBefore: byeRecipient.record,
						player2Id: byePlayer.id,
						player2RecordBefore: '0-0',
						winnerId: byeRecipient.id,
					},
				})
				await prisma.player.update({
					where: { id: byeRecipient.id },
					data: {
						score: { increment: 1 },
						record: computeNewRecord(byeRecipient.record, true),
					},
				})
				pairingPool = activePlayers.filter(
					(p) => p.playerNumber !== byePlayerNum,
				)
			}

			const pairingInputs = pairingPool.map((p) => ({
				playerNumber: p.playerNumber!,
				score: p.score,
				opponents: opponentsMap.get(p.id) ?? [],
			}))

			const pairs = pairPlayers(pairingInputs)

			await prisma.roundPairing.createMany({
				data: pairs.map(([n1, n2]) => ({
					roundId: round.id,
					tournamentId,
					player1Id: playerByNumber.get(n1)!.id,
					player1RecordBefore: playerByNumber.get(n1)!.record,
					player2Id: playerByNumber.get(n2)!.id,
					player2RecordBefore: playerByNumber.get(n2)!.record,
				})),
			})

			return redirect(`/tournaments/results?id=${tournamentId}`)
		}
		case 'finish-tournament': {
			const tournamentId = String(formData.get('tournamentId'))

			const tournament = await prisma.tournament.findUnique({
				where: { id: tournamentId },
				include: {
					players: true,
					pairings: {
						include: { player1: true, player2: true },
					},
				},
			})
			if (!tournament)
				return redirect(`/tournaments/results?id=${tournamentId}`)

			// Build TiebreakInput for each real player (exclude bye pseudo-player)
			const tiebreakInputs = tournament.players
				.filter((p) => !p.isByePlayer)
				.map((player) => {
					const pairings = tournament.pairings
						.filter(
							(pair) =>
								pair.player1Id === player.id || pair.player2Id === player.id,
						)
						.map((pair) => {
							const isP1 = pair.player1Id === player.id
							const opponent = isP1 ? pair.player2 : pair.player1
							return {
								opponentId: opponent.id,
								opponentScore: opponent.score,
								playerWon: pair.winnerId === player.id,
							}
						})
					return {
						playerId: player.id,
						score: player.score,
						tieBreakScore: player.tieBreakScore,
						pairings,
					}
				})

			const results = computeTiebreaks(tiebreakInputs)

			await Promise.all(
				results.map((r) =>
					prisma.player.update({
						where: { id: r.playerId },
						data: {
							bScore: r.bScore,
							sbScore: r.sbScore,
							position: r.position,
						},
					}),
				),
			)

			const winner = results.find((r) => r.position === 1)
			const second = results.find((r) => r.position === 2)

			await prisma.tournament.update({
				where: { id: tournamentId },
				data: {
					finished: true,
					winnerId: winner?.playerId,
					secondPlaceId: second?.playerId,
				},
			})

			return redirect(`/tournaments/results?id=${tournamentId}`)
		}
		default:
			return redirect('/tournaments/results')
	}
}

type Tournament = NonNullable<Awaited<ReturnType<typeof loader>>['tournament']>
type Round = Tournament['roundsList'][number]
type Pairing = Round['pairings'][number]

export default function TournamentResults({
	loaderData,
}: Route.ComponentProps) {
	const { tournament, allTournaments, creatingNew, isAdmin } = loaderData
	const navigate = useNavigate()
	const [pendingWinners, setPendingWinners] = useState<Record<string, string>>(
		{},
	)

	return (
		<div className="mx-auto max-w-5xl px-4">
			<div className="mb-8 flex items-center gap-3">
				{allTournaments.length > 0 && !creatingNew ? (
					<select
						value={tournament?.id ?? ''}
						onChange={(e) => navigate(`?id=${e.target.value}`)}
						className="text-muted-foreground hover:text-foreground rounded-md border border-slate-200 bg-transparent px-3 py-2 text-base dark:border-slate-700"
					>
						{allTournaments.map((t) => (
							<option key={t.id} value={t.id}>
								{t.name}
								{t.finished ? ' ✓' : ''}
							</option>
						))}
					</select>
				) : null}
				{isAdmin && tournament && !creatingNew ? (
					<a
						href="?new=1"
						className="hover:bg-accent hover:text-accent-foreground inline-flex h-9 items-center rounded-md px-3 text-sm font-medium"
					>
						New
					</a>
				) : null}
			</div>
			{isAdmin && creatingNew ? (
				<div className="max-w-2xl">
					<NewTournamentForm />
				</div>
			) : !tournament ? (
				<div className="flex items-center gap-3">
					<p className="text-muted-foreground">No tournaments yet.</p>
					{isAdmin ? (
						<a
							href="?new=1"
							className="hover:bg-accent hover:text-accent-foreground inline-flex h-9 items-center rounded-md px-3 text-sm font-medium"
						>
							New
						</a>
					) : null}
				</div>
			) : tournament.started ? (
				<div className="flex flex-col gap-8">
					<div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-start">
						<DetailsSection
							tournament={tournament}
							editing={loaderData.editing}
							isAdmin={isAdmin}
						/>
						<CurrentRecordsSection
							tournament={tournament}
							isAdmin={isAdmin}
							pendingWinners={pendingWinners}
						/>
					</div>
					{tournament.roundsList.length > 0 ? (
						<RoundsArea
							rounds={tournament.roundsList}
							tournament={tournament}
							isAdmin={isAdmin}
							pendingWinners={pendingWinners}
							setPendingWinners={setPendingWinners}
						/>
					) : null}
				</div>
			) : (
				<div className="flex flex-col gap-8 lg:flex-row lg:items-start">
					<div className="shrink-0 lg:w-80">
						<DetailsSection
							tournament={tournament}
							editing={loaderData.editing}
							isAdmin={isAdmin}
						/>
					</div>
					<div className="flex-1">
						<PlayersSection tournament={tournament} isAdmin={isAdmin} />
					</div>
				</div>
			)}
		</div>
	)
}

function NewTournamentForm() {
	return (
		<section className="rounded-xl border border-slate-200 p-6 dark:border-slate-700">
			<h2 className="mb-4 text-xl font-semibold">New Tournament</h2>
			<Form method="POST" className="flex flex-col gap-4">
				<input type="hidden" name="intent" value="create-tournament" />
				<div className="flex flex-col gap-1">
					<label htmlFor="name" className="text-sm font-medium">
						Name
					</label>
					<input
						id="name"
						name="name"
						type="text"
						required
						placeholder="e.g. April 2026 Swiss"
						className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
					/>
				</div>
				<div className="flex gap-4">
					<div className="flex flex-1 flex-col gap-1">
						<label htmlFor="date" className="text-sm font-medium">
							Date
						</label>
						<input
							id="date"
							name="date"
							type="date"
							required
							className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
						/>
					</div>
					<div className="flex flex-1 flex-col gap-1">
						<label htmlFor="startTime" className="text-sm font-medium">
							Start Time
						</label>
						<input
							id="startTime"
							name="startTime"
							type="time"
							className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
						/>
					</div>
				</div>
				<div className="flex gap-4">
					<div className="flex flex-1 flex-col gap-1">
						<label htmlFor="matchLength" className="text-sm font-medium">
							Match Length
						</label>
						<input
							id="matchLength"
							name="matchLength"
							type="number"
							min={1}
							className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
						/>
					</div>
					<div className="flex flex-1 flex-col gap-1">
						<label htmlFor="rounds" className="text-sm font-medium">
							Rounds
						</label>
						<input
							id="rounds"
							name="rounds"
							type="number"
							min={1}
							defaultValue={4}
							className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
						/>
					</div>
				</div>
				<Button type="submit" className="self-start">
					Create Tournament
				</Button>
			</Form>
		</section>
	)
}

export function DetailsSection({
	tournament,
	editing,
	isAdmin,
}: {
	tournament: Tournament
	editing: boolean
	isAdmin: boolean
}) {
	const d = new Date(tournament.date)
	const MONTHS = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	]
	const displayDate = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`
	const dateValue = d.toISOString().split('T')[0]

	if (isAdmin && editing) {
		return (
			<section className="rounded-xl border border-slate-200 p-6 dark:border-slate-700">
				<h2 className="mb-4 text-xl font-semibold">Details</h2>
				<Form method="POST" className="flex flex-col gap-4">
					<input type="hidden" name="intent" value="update-details" />
					<input type="hidden" name="tournamentId" value={tournament.id} />
					<div className="flex flex-col gap-1">
						<label htmlFor="edit-name" className="text-sm font-medium">
							Name
						</label>
						<input
							id="edit-name"
							name="name"
							type="text"
							required
							autoFocus
							defaultValue={tournament.name}
							className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
						/>
					</div>
					<div className="flex flex-col gap-4">
						<div className="flex flex-col gap-1">
							<label htmlFor="edit-date" className="text-sm font-medium">
								Date
							</label>
							<input
								id="edit-date"
								name="date"
								type="date"
								required
								defaultValue={dateValue}
								className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label htmlFor="edit-startTime" className="text-sm font-medium">
								Start Time
							</label>
							<input
								id="edit-startTime"
								name="startTime"
								type="time"
								defaultValue={tournament.startTime ?? ''}
								className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label htmlFor="edit-matchLength" className="text-sm font-medium">
								Match Length
							</label>
							<input
								id="edit-matchLength"
								name="matchLength"
								type="number"
								min={1}
								defaultValue={tournament.matchLength ?? ''}
								className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
							/>
						</div>
						<div className="flex flex-col gap-1">
							<label htmlFor="edit-rounds" className="text-sm font-medium">
								Rounds
							</label>
							<input
								id="edit-rounds"
								name="rounds"
								type="number"
								min={1}
								defaultValue={tournament.rounds}
								className="rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
							/>
						</div>
					</div>
					<div className="flex gap-2">
						<Button type="submit" size="sm">
							Save
						</Button>
						<a
							href={`/tournaments/results?id=${tournament.id}`}
							className="inline-flex h-9 items-center rounded-md border border-slate-300 px-3 text-sm font-medium dark:border-slate-600"
						>
							Cancel
						</a>
					</div>
				</Form>
			</section>
		)
	}

	return (
		<section className="rounded-xl border border-slate-200 p-6 dark:border-slate-700">
			<div className="flex items-center justify-between">
				<h2 className="text-xl font-semibold">Details</h2>
				{isAdmin && !tournament.finished ? (
					<a
						href={`?id=${tournament.id}&edit=1`}
						className="text-muted-foreground hover:text-foreground"
						aria-label="Edit details"
					>
						<Icon name="pencil-1" size="sm" />
					</a>
				) : null}
			</div>
			<dl className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
				<div className="col-span-2">
					<dt className="text-muted-foreground font-medium">Name</dt>
					<dd className="mt-0.5 text-base font-semibold">{tournament.name}</dd>
				</div>
				<div>
					<dt className="text-muted-foreground font-medium">Date</dt>
					<dd className="mt-0.5">{displayDate}</dd>
				</div>
				{tournament.startTime ? (
					<div>
						<dt className="text-muted-foreground font-medium">Start Time</dt>
						<dd className="mt-0.5">{tournament.startTime}</dd>
					</div>
				) : null}
				{tournament.matchLength ? (
					<div>
						<dt className="text-muted-foreground font-medium">Match Length</dt>
						<dd className="mt-0.5">{tournament.matchLength}</dd>
					</div>
				) : null}
				<div>
					<dt className="text-muted-foreground font-medium">Rounds</dt>
					<dd className="mt-0.5">{tournament.rounds}</dd>
				</div>
				{tournament.started && !tournament.finished ? (
					<div>
						<dt className="text-muted-foreground font-medium">Current Round</dt>
						<dd className="mt-0.5">{tournament.currentRound}</dd>
					</div>
				) : null}
				{tournament.finished && tournament.winner ? (
					<div>
						<dt className="text-muted-foreground font-medium">Winner</dt>
						<dd className="mt-0.5 font-semibold text-green-600 dark:text-green-400">
							{tournament.winner.name}
						</dd>
					</div>
				) : null}
				{tournament.finished && tournament.secondPlace ? (
					<div>
						<dt className="text-muted-foreground font-medium">Second Place</dt>
						<dd className="mt-0.5 font-semibold text-green-600 dark:text-green-400">
							{tournament.secondPlace.name}
						</dd>
					</div>
				) : null}
			</dl>
		</section>
	)
}

function PlayersSection({
	tournament,
	isAdmin,
}: {
	tournament: Tournament
	isAdmin: boolean
}) {
	const realPlayers = tournament.players.filter((p) => !p.isByePlayer)
	const unpaidCount = realPlayers.filter((p) => !p.paid).length

	return (
		<section className="rounded-xl border border-slate-200 p-6 dark:border-slate-700">
			<h2 className="mb-4 text-xl font-semibold">Players</h2>

			{isAdmin ? (
				<Form
					key={realPlayers.length}
					method="POST"
					className="mb-6 flex gap-2"
				>
					<input type="hidden" name="intent" value="add-player" />
					<input type="hidden" name="tournamentId" value={tournament.id} />
					<input
						name="playerName"
						type="text"
						placeholder="Player name"
						required
						autoFocus
						className="flex-1 rounded-md border border-slate-300 px-3 py-2 dark:border-slate-600 dark:bg-slate-800"
					/>
					<Button type="submit" size="sm">
						<Icon name="plus" size="sm" />
						Add
					</Button>
				</Form>
			) : null}

			{realPlayers.length > 0 ? (
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-slate-200 dark:border-slate-700">
							<th className="pb-2 text-left font-medium">Player</th>
							<th className="pb-2 text-center font-medium">Paid</th>
							{isAdmin ? <th className="pb-2" /> : null}
						</tr>
					</thead>
					<tbody>
						{realPlayers.map((player) => (
							<PlayerRow key={player.id} player={player} isAdmin={isAdmin} />
						))}
					</tbody>
				</table>
			) : (
				<p className="text-muted-foreground text-sm">No players yet.</p>
			)}

			{isAdmin && realPlayers.length > 0 ? (
				<div className="mt-6 flex items-center gap-4">
					<Form method="POST">
						<input type="hidden" name="intent" value="start-tournament" />
						<input type="hidden" name="tournamentId" value={tournament.id} />
						<Button type="submit" disabled={unpaidCount > 0}>
							Start Tournament
						</Button>
					</Form>
					{unpaidCount > 0 ? (
						<p className="text-sm text-amber-600 dark:text-amber-400">
							{unpaidCount} player{unpaidCount !== 1 ? 's have' : ' has'} not
							paid yet.
						</p>
					) : null}
				</div>
			) : null}
		</section>
	)
}

export function CurrentRecordsSection({
	tournament,
	isAdmin,
	pendingWinners,
}: {
	tournament: Tournament
	isAdmin: boolean
	pendingWinners: Record<string, string>
}) {
	const [playerToDrop, setPlayerToDrop] = useState<{
		id: string
		name: string
	} | null>(null)

	const currentRound = tournament.roundsList.find(
		(r) => r.number === tournament.currentRound,
	)
	const currentRoundReported = Boolean(currentRound?.timeEnded)
	const hasMoreRounds = tournament.currentRound < tournament.rounds
	const onLastRound = !hasMoreRounds && !tournament.finished

	// Build per-player round record info from current round pairings
	const playerRoundInfo = new Map<
		string,
		{ lastRnd: string; currRnd: string | null }
	>()
	if (currentRound) {
		for (const pairing of currentRound.pairings) {
			const isBye = pairing.player2.isByePlayer
			const resolvedWinner = pairing.winnerId ?? pendingWinners[pairing.id]

			const p1Won = resolvedWinner === pairing.player1.id
			const p2Won = resolvedWinner === pairing.player2.id

			playerRoundInfo.set(pairing.player1.id, {
				lastRnd: pairing.player1RecordBefore,
				currRnd:
					p1Won || p2Won
						? computeNewRecord(pairing.player1RecordBefore, p1Won)
						: null,
			})

			if (!isBye) {
				playerRoundInfo.set(pairing.player2.id, {
					lastRnd: pairing.player2RecordBefore,
					currRnd:
						p1Won || p2Won
							? computeNewRecord(pairing.player2RecordBefore, p2Won)
							: null,
				})
			}
		}
	}

	if (tournament.finished) {
		const sorted = [...tournament.players]
			.filter((p) => !p.isByePlayer)
			.sort((a, b) => a.position - b.position)
		return (
			<section className="rounded-xl border border-slate-200 p-6 dark:border-slate-700">
				<h2 className="mb-4 text-xl font-semibold">Final Standings</h2>
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-slate-200 dark:border-slate-700">
							<th className="pb-2 text-left font-medium">Player</th>
							<th className="w-16 pb-2 text-right font-medium">Record</th>
							<th className="w-16 pb-2 text-right font-medium">B</th>
							<th className="w-16 pb-2 text-right font-medium">SB</th>
							<th className="w-16 pb-2 text-right font-medium">Pos</th>
						</tr>
					</thead>
					<tbody>
						{sorted.map((player) => (
							<tr
								key={player.id}
								className={`border-b border-slate-100 last:border-0 dark:border-slate-800 ${player.droppedOut ? 'opacity-40' : ''}`}
							>
								<td className="py-2">
									<span className="flex items-center gap-1.5">
										{player.name}
										{player.droppedOut ? (
											<span className="text-xs italic">(dropped)</span>
										) : player.position === 1 || player.position === 2 ? (
											<span className="font-bold text-green-600 dark:text-green-400">
												{player.position}
											</span>
										) : null}
									</span>
								</td>
								<td className="py-2 text-right font-mono">{player.record}</td>
								<td className="py-2 text-right font-mono">{player.bScore}</td>
								<td className="py-2 text-right font-mono">{player.sbScore}</td>
								<td className="py-2 text-right font-mono">{player.position}</td>
							</tr>
						))}
					</tbody>
				</table>
			</section>
		)
	}

	const wins = (record: string | null | undefined) =>
		record ? parseInt(record.split('-')[0] ?? '0', 10) : -1

	const sorted = [...tournament.players]
		.filter((p) => !p.isByePlayer)
		.sort((a, b) => {
			const aCurr = playerRoundInfo.get(a.id)?.currRnd ?? null
			const bCurr = playerRoundInfo.get(b.id)?.currRnd ?? null
			const aLast = playerRoundInfo.get(a.id)?.lastRnd ?? null
			const bLast = playerRoundInfo.get(b.id)?.lastRnd ?? null
			return (
				wins(bCurr) - wins(aCurr) ||
				wins(bLast) - wins(aLast) ||
				(a.playerNumber ?? 0) - (b.playerNumber ?? 0)
			)
		})

	return (
		<section className="rounded-xl border border-slate-200 p-6 dark:border-slate-700">
			<h2 className="mb-4 text-xl font-semibold">Current Records</h2>
			<table className="w-full text-sm">
				<thead>
					<tr className="border-b border-slate-200 dark:border-slate-700">
						<th className="pb-2 text-left font-medium">Player</th>
						<th className="w-24 pb-2 text-center font-medium">Last Rnd</th>
						<th className="w-24 pb-2 text-center font-medium">Curr Rnd</th>
						{isAdmin ? <th className="pb-2" /> : null}
					</tr>
				</thead>
				<tbody>
					{sorted.map((player) => {
						const info = playerRoundInfo.get(player.id)
						return (
							<tr
								key={player.id}
								className={`border-b border-slate-100 last:border-0 dark:border-slate-800 ${player.droppedOut ? 'opacity-40' : ''}`}
							>
								<td className="py-2">
									{player.droppedOut ? (
										<span>
											{player.name}{' '}
											<span className="text-xs italic">(dropped)</span>
										</span>
									) : (
										player.name
									)}
								</td>
								<td className="py-2 text-center font-mono font-semibold">
									{info?.lastRnd ?? player.record}
								</td>
								<td
									className={cn(
										'py-2 text-center font-mono',
										info?.currRnd ? 'font-semibold' : 'text-muted-foreground',
									)}
								>
									{info?.currRnd ?? '—'}
								</td>
								{isAdmin ? (
									<td className="py-2 text-right">
										{!player.droppedOut ? (
											<button
												type="button"
												aria-label="Drop player"
												className="text-muted-foreground hover:text-destructive text-xs"
												onClick={() =>
													setPlayerToDrop({ id: player.id, name: player.name })
												}
											>
												Drop
											</button>
										) : null}
									</td>
								) : null}
							</tr>
						)
					})}
				</tbody>
			</table>

			<Dialog
				open={playerToDrop !== null}
				onOpenChange={(open) => {
					if (!open) setPlayerToDrop(null)
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Drop {playerToDrop?.name}?</DialogTitle>
						<DialogDescription>
							Their opponent will receive a win. This cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="mt-4">
						<Button variant="outline" onClick={() => setPlayerToDrop(null)}>
							Cancel
						</Button>
						<Form method="POST" onSubmit={() => setPlayerToDrop(null)}>
							<input type="hidden" name="intent" value="drop-player" />
							<input
								type="hidden"
								name="playerId"
								value={playerToDrop?.id ?? ''}
							/>
							<Button type="submit" variant="destructive">
								Drop
							</Button>
						</Form>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{isAdmin && hasMoreRounds ? (
				<div className="mt-4">
					<Form method="POST">
						<input type="hidden" name="intent" value="next-round" />
						<input type="hidden" name="tournamentId" value={tournament.id} />
						<Button type="submit" size="sm" disabled={!currentRoundReported}>
							Next Round
						</Button>
					</Form>
				</div>
			) : null}
			{isAdmin && onLastRound ? (
				<div className="mt-4">
					<Form method="POST">
						<input type="hidden" name="intent" value="finish-tournament" />
						<input type="hidden" name="tournamentId" value={tournament.id} />
						<Button type="submit" size="sm" disabled={!currentRoundReported}>
							Finish Tournament
						</Button>
					</Form>
				</div>
			) : null}
		</section>
	)
}

function RoundsArea({
	rounds,
	tournament,
	isAdmin,
	pendingWinners,
	setPendingWinners,
}: {
	rounds: Round[]
	tournament: Tournament
	isAdmin: boolean
	pendingWinners: Record<string, string>
	setPendingWinners: React.Dispatch<
		React.SetStateAction<Record<string, string>>
	>
}) {
	return (
		<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
			{rounds.map((round) => (
				<RoundSection
					key={round.id}
					round={round}
					tournamentId={tournament.id}
					isAdmin={isAdmin}
					pendingWinners={pendingWinners}
					setPendingWinners={setPendingWinners}
				/>
			))}
		</div>
	)
}

function RoundSection({
	round,
	tournamentId,
	isAdmin,
	pendingWinners,
	setPendingWinners,
}: {
	round: Round
	tournamentId: string
	isAdmin: boolean
	pendingWinners: Record<string, string>
	setPendingWinners: React.Dispatch<
		React.SetStateAction<Record<string, string>>
	>
}) {
	const isReported = Boolean(round.timeEnded)
	const realPairings = round.pairings.filter((p) => !p.player2.isByePlayer)
	const allSelected =
		realPairings.length > 0 &&
		realPairings.every((p) => p.id in pendingWinners || Boolean(p.winnerId))

	return (
		<section className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
			<h2 className="mb-3 text-lg font-semibold">Round {round.number}</h2>
			<div className="grid [grid-template-columns:repeat(auto-fill,minmax(200px,1fr))] gap-3">
				{round.pairings.map((pairing) => (
					<PairingCard
						key={pairing.id}
						pairing={pairing}
						winnerId={pendingWinners[pairing.id]}
						isReported={isReported}
						isAdmin={isAdmin}
						onSelect={(playerId) =>
							setPendingWinners((prev) => ({ ...prev, [pairing.id]: playerId }))
						}
						onUnselect={() =>
							setPendingWinners((prev) => {
								const next = { ...prev }
								delete next[pairing.id]
								return next
							})
						}
					/>
				))}
			</div>
			{isAdmin && !isReported ? (
				<Form method="POST" className="mt-3">
					<input type="hidden" name="intent" value="report-results" />
					<input type="hidden" name="tournamentId" value={tournamentId} />
					<input type="hidden" name="roundId" value={round.id} />
					{Object.entries(pendingWinners).map(([pairingId, playerId]) => (
						<input
							key={pairingId}
							type="hidden"
							name={`winner_${pairingId}`}
							value={playerId}
						/>
					))}
					<Button type="submit" size="sm" disabled={!allSelected}>
						Report Results
					</Button>
				</Form>
			) : null}
		</section>
	)
}

function PairingCard({
	pairing,
	winnerId,
	isReported,
	isAdmin,
	onSelect,
	onUnselect,
}: {
	pairing: Pairing
	winnerId: string | undefined
	isReported: boolean
	isAdmin: boolean
	onSelect: (playerId: string) => void
	onUnselect: () => void
}) {
	const { player1, player2 } = pairing
	const r1 = pairing.player1RecordBefore
	const r2 = pairing.player2RecordBefore

	// Bye pairing — player1 gets the free win
	if (player2.isByePlayer) {
		return (
			<div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
				<div className="flex items-center justify-between gap-2">
					<span className="text-sm font-medium">
						{player1.name} ({r1})
					</span>
					<Icon name="check" size="xl" className="text-green-500" />
				</div>
				<div className="text-muted-foreground mt-1 text-xs">BYE</div>
			</div>
		)
	}

	const committedWinnerId = pairing.winnerId
	const pendingWinnerId = winnerId
	const resolvedWinnerId = committedWinnerId ?? pendingWinnerId
	const p1Won = resolvedWinnerId === player1.id
	const p2Won = resolvedWinnerId === player2.id
	const isPending = !committedWinnerId && Boolean(pendingWinnerId)
	const showWButtons = isAdmin && !isReported && !resolvedWinnerId

	return (
		<div className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
			{/* Player 1 */}
			<div className="flex items-center justify-between gap-2 py-1">
				<span
					className={cn(
						'text-sm whitespace-nowrap',
						p2Won && 'text-muted-foreground',
					)}
				>
					{player1.name} ({r1})
				</span>
				{p1Won ? (
					<span className="flex shrink-0 items-center gap-1">
						<Icon name="check" size="xl" className="text-green-500" />
						{isPending ? (
							<button
								type="button"
								onClick={onUnselect}
								aria-label="Undo result"
								className="text-muted-foreground hover:text-foreground"
							>
								<Icon name="pencil-1" size="sm" />
							</button>
						) : null}
					</span>
				) : showWButtons ? (
					<button
						type="button"
						onClick={() => onSelect(player1.id)}
						className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-xs hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
					>
						W
					</button>
				) : null}
			</div>

			<div className="border-t border-slate-100 dark:border-slate-800" />

			{/* Player 2 */}
			<div className="flex items-center justify-between gap-2 py-1">
				<span
					className={cn(
						'text-sm whitespace-nowrap',
						p1Won && 'text-muted-foreground',
					)}
				>
					{player2.name} ({r2})
				</span>
				{p2Won ? (
					<span className="flex shrink-0 items-center gap-1">
						<Icon name="check" size="xl" className="text-green-500" />
						{isPending ? (
							<button
								type="button"
								onClick={onUnselect}
								aria-label="Undo result"
								className="text-muted-foreground hover:text-foreground"
							>
								<Icon name="pencil-1" size="sm" />
							</button>
						) : null}
					</span>
				) : showWButtons ? (
					<button
						type="button"
						onClick={() => onSelect(player2.id)}
						className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-xs hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
					>
						W
					</button>
				) : null}
			</div>
		</div>
	)
}

type Player = Tournament['players'][number]

function PlayerRow({ player, isAdmin }: { player: Player; isAdmin: boolean }) {
	return (
		<tr className="border-b border-slate-100 last:border-0 dark:border-slate-800">
			<td className="py-2">{player.name}</td>
			<td className="py-2 text-center">
				{isAdmin ? (
					<Form method="POST">
						<input type="hidden" name="intent" value="toggle-paid" />
						<input type="hidden" name="playerId" value={player.id} />
						<input type="hidden" name="paid" value={String(!player.paid)} />
						<button
							type="submit"
							aria-label={player.paid ? 'Mark unpaid' : 'Mark paid'}
							className={
								player.paid
									? 'text-green-600 dark:text-green-400'
									: 'text-muted-foreground'
							}
						>
							<Icon name={player.paid ? 'check' : 'cross-1'} size="sm" />
						</button>
					</Form>
				) : (
					<Icon
						name={player.paid ? 'check' : 'cross-1'}
						size="sm"
						className={
							player.paid
								? 'text-green-600 dark:text-green-400'
								: 'text-muted-foreground'
						}
					/>
				)}
			</td>
			{isAdmin ? (
				<td className="py-2 text-right">
					<Form method="POST">
						<input type="hidden" name="intent" value="remove-player" />
						<input type="hidden" name="playerId" value={player.id} />
						<button
							type="submit"
							aria-label="Remove player"
							className="text-muted-foreground hover:text-destructive"
						>
							<Icon name="trash" size="sm" />
						</button>
					</Form>
				</td>
			) : null}
		</tr>
	)
}
